import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import {
  baseI18nConfig,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  type LanguageInfo,
} from '@grade/i18n';

// Función para verificar si un idioma es soportado
const isLanguageSupported = (lng: string): lng is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.some((lang: LanguageInfo) => lang.code === lng);
};

// Configuración específica para el dashboard web
const dashboardConfig = {
  ...baseI18nConfig,
  detection: {
    order: ['querystring', 'localStorage', 'sessionStorage', 'navigator'],
    lookupQuerystring: 'lng',
    lookupLocalStorage: 'dashboard-language',
    lookupSessionStorage: 'dashboard-language',
    caches: ['localStorage', 'sessionStorage'],
    convertDetectedLanguage: (lng: string) => {
      // Asegurar que solo usamos idiomas soportados
      return isLanguageSupported(lng) ? lng : 'en';
    },
  },
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
    addPath: '/locales/{{lng}}/{{ns}}.missing.json',
  },
  ns: ['common', 'auth', 'dashboard'],
  defaultNS: 'dashboard',
  interpolation: {
    escapeValue: false,
    format: (value: any, format?: string) => {
      if (format === 'uppercase') return value.toUpperCase();
      if (format === 'lowercase') return value.toLowerCase();
      if (format === 'currency') {
        return new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      }
      if (format === 'date') {
        return new Intl.DateTimeFormat(i18n.language).format(new Date(value));
      }
      if (format === 'datetime') {
        return new Intl.DateTimeFormat(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(value));
      }
      return value;
    },
  },
};

// Inicializar i18next para el dashboard
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(dashboardConfig);

// Función para cambiar idioma con persistencia
export const changeLanguage = async (lng: SupportedLanguage): Promise<void> => {
  await i18n.changeLanguage(lng);
  localStorage.setItem('dashboard-language', lng);
  sessionStorage.setItem('dashboard-language', lng);

  // Actualizar el atributo lang del documento
  document.documentElement.lang = lng;

  // Dispatch custom event para componentes que necesiten reaccionar
  window.dispatchEvent(
    new CustomEvent('languageChanged', {
      detail: { language: lng },
    })
  );
};

// Función para obtener el idioma actual
export const getCurrentLanguage = (): SupportedLanguage => {
  return i18n.language as SupportedLanguage;
};

// Función para obtener la dirección del texto (para futuros idiomas RTL)
export const getTextDirection = (): 'ltr' | 'rtl' => {
  const rtlLanguages = ['ar', 'he', 'fa']; // Árabe, Hebreo, Persa
  return rtlLanguages.includes(i18n.language) ? 'rtl' : 'ltr';
};

// Hook personalizado para traducir con soporte de números y fechas
export const useTranslationWithFormat = () => {
  const { t } = require('react-i18next');

  return {
    t,
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(i18n.language, options).format(value);
    },
    formatCurrency: (value: number, currency = 'USD') => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
      }).format(value);
    },
    formatDate: (
      value: Date | string | number,
      options?: Intl.DateTimeFormatOptions
    ) => {
      return new Intl.DateTimeFormat(i18n.language, options).format(
        new Date(value)
      );
    },
    formatRelativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit) => {
      return new Intl.RelativeTimeFormat(i18n.language, {
        numeric: 'auto',
      }).format(value, unit);
    },
  };
};

export default i18n;
